<template>
  <div class="category-page">
    <div class="intro-box">
      <p class="eyebrow">Kategorie</p>
      <h1>{{ categoryTitle }}</h1>
      <p v-if="categoryDescription" class="category-description" v-html="categoryDescription"></p>
    </div>

    <div v-if="loading" class="loading-message">
      Produkte werden geladen...
    </div>

    <div v-else-if="error" class="error-message">
      Fehler beim Laden der Produkte: {{ error }}
    </div>

    <div v-else>
      <div v-if="!filteredProducts.length" class="empty-state">
        Zu dieser Kategorie sind aktuell keine Produkte verfügbar.
      </div>

      <div v-else class="product-grid">
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          @click="goToDetail(product.id)"
          class="product-card"
        >
          <div class="image-wrapper">
            <img
              :src="(product.images && product.images[0]) || product.image"
              :alt="product.name"
              class="product-image"
              @error="onImageError"
            >
          </div>

          <div class="card-content">
            <div class="card-header">
              <h2 class="product-name">{{ product.name }}</h2>
              <span class="product-price">{{ formatCurrency(product.price) }}</span>
            </div>

            <p class="product-description">{{ product.description }}</p>

            <div v-if="product.tags && product.tags.length" class="card-tags">
              <span
                v-for="tag in product.tags"
                :key="tag.id"
                class="card-tag"
                :style="{ backgroundColor: tag.color || '#3498db' }"
              >
                {{ tag.name }}
              </span>
            </div>

            <button class="details-btn" type="button">Details ansehen</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useConfig } from '../composables/useConfig.js';
import { getProducts } from '../services/dbApi';

const route = useRoute();
const router = useRouter();
const { config, loadConfig } = useConfig();

const products = ref([]);
const loading = ref(true);
const error = ref(null);

const slugify = (value) => {
  return String(value || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'kategorie';
};

const categorySlug = computed(() => slugify(route.params.slug || ''));

const categoryConfig = computed(() => {
  const categories = Array.isArray(config?.homepage?.categories) ? config.homepage.categories : [];
  const routeTagId = route.query.tagId;

  return categories.find(category => {
    const sameSlug = slugify(category?.name || '') === categorySlug.value;
    const sameTag = routeTagId !== undefined && routeTagId !== null && routeTagId !== ''
      ? String(category?.tagId || '') === String(routeTagId)
      : false;
    return sameSlug || sameTag;
  }) || null;
});

const categoryTitle = computed(() => categoryConfig.value?.name || 'Produktkategorie');
const categoryDescription = computed(() => categoryConfig.value?.description || '');

const categoryTagId = computed(() => {
  if (categoryConfig.value && categoryConfig.value.tagId !== undefined && categoryConfig.value.tagId !== null && categoryConfig.value.tagId !== '') {
    return Number(categoryConfig.value.tagId);
  }

  const routeTagId = route.query.tagId;
  if (routeTagId !== undefined && routeTagId !== null && routeTagId !== '') {
    return Number(routeTagId);
  }

  const targetName = (categoryTitle.value || '').toLowerCase();
  const match = products.value
    .flatMap(product => Array.isArray(product.tags) ? product.tags : [])
    .find(tag => (tag.name || '').toLowerCase() === targetName);

  return match ? Number(match.id) : null;
});

const filteredProducts = computed(() => {
  if (categoryTagId.value === null || Number.isNaN(categoryTagId.value)) {
    return [];
  }

  return products.value.filter(product => {
    const productTagIds = Array.isArray(product.tags) ? product.tags.map(tag => Number(tag.id)) : [];
    return productTagIds.includes(Number(categoryTagId.value));
  });
});

const loadProducts = async () => {
  try {
    loading.value = true;
    error.value = null;
    products.value = await getProducts();
  } catch (err) {
    console.error('Error loading products:', err);
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadConfig();
  loadProducts();
});

const onImageError = (event) => {
  event.target.src = 'https://placehold.co/600x400/cccccc/333333?text=Bild+Fehlt';
};

const goToDetail = (productId) => {
  router.push({ name: 'ProductDetail', params: { id: productId } });
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));
};
</script>

<style scoped>
.category-page {
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;
}

.intro-box {
  background-color: var(--page-background-color, #f9f9f9);
  padding: 30px;
  border-radius: 12px;
  border: 1px solid #eee;
  margin-bottom: 32px;
  text-align: center;
}

.eyebrow {
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: var(--interactive-accent-color, #8b6f47);
  font-weight: 700;
}

.intro-box h1 {
  margin: 0 0 10px;
  color: var(--theme-primary-color, #222);
  line-height: 1.2;
}

.category-description {
  max-width: 760px;
  margin: 0 auto;
  line-height: 1.6;
  color: var(--body-text-color, #444);
}

.loading-message,
.empty-state,
.error-message {
  background: #f7f7f7;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  padding: 18px;
  text-align: center;
  color: var(--body-text-color, #444);
}

.error-message {
  color: #a33;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 22px;
}

.product-card {
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.image-wrapper {
  width: 100%;
  height: 220px;
  background: #f2f2f2;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-content {
  padding: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.product-name {
  margin: 0;
  font-size: 1.1rem;
  color: var(--theme-primary-color, #222);
}

.product-price {
  font-weight: 700;
  color: var(--interactive-accent-color, #8b6f47);
  white-space: nowrap;
}

.product-description {
  margin: 0 0 12px;
  color: var(--body-text-color, #444);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

.card-tag {
  display: inline-block;
  color: #fff;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.details-btn {
  width: 100%;
  border: none;
  border-radius: 8px;
  background: var(--button-background-color, #8b6f47);
  color: var(--button-label-color, #fff);
  padding: 10px 12px;
  font-weight: 600;
  cursor: pointer;
}
</style>
